/* Albümler — liste, düzenleme, kare seçimi, sıralama.

   ── Sıralama neden elle yazıldı ─────────────────────────────

   Plan §8 `dnd-kit` diyor. Buradaki iş HTML5'in kendi sürükle-bırak
   olaylarıyla yapılıyor: `draggable` + `dragstart`/`dragover`/`drop`, otuz
   satır. Bir kütüphane dokunmatik ve klavye desteğini bedavaya verirdi ama
   iki şey onu bu ekranda gereksiz kılıyor: panel masaüstünde kullanılıyor
   (2 GB'lık bir yığın telefondan düzenlenmiyor) ve klavye erişimi ayrıca
   veriliyor — her karenin yanında "sola/sağa taşı" düğmeleri var, ki
   sürüklemek zaten erişilebilir bir etkileşim değil.

   ── Sıra sunucuya nasıl gidiyor ─────────────────────────────

   `POST /api/admin/albums/:id/reorder` gövdesi albümün **tamamını** anlatır:
   listede olmayan kare albümden çıkar. Panel bu yüzden tek bir "kaydet"
   düğmesiyle çalışıyor — her sürüklemede istek atmak, yarım kalmış bir
   düzenlemeyi sunucuya yazmak olurdu. Sunucu tarafında yazma tek bir SQL
   ifadesi, yani reddedilen bir sıralama albümü olduğu gibi bırakıyor. */

import type { AlbumDetail, AlbumSummary } from '@frawgraphy/shared';
import { albums, photos } from '../api.ts';
import type { PhotoRow } from '../api.ts';
import { el, replace, when } from '../dom.ts';
import type { AppContext } from '../main.ts';

export async function renderAlbums(host: HTMLElement, ctx: AppContext): Promise<void> {
  /* `#/albumler/alb_x` → düzenleme ekranı; yalnızca `#/albumler` → liste. */
  const id = location.hash.split('/')[2];
  if (id) {
    await renderEditor(host, ctx, decodeURIComponent(id));
    return;
  }
  await renderList(host, ctx);
}

/* ── Liste ──────────────────────────────────────────────── */

async function renderList(host: HTMLElement, ctx: AppContext): Promise<void> {
  replace(host, el('h1', { text: 'Albümler' }), el('p', { class: 'note', text: 'Okunuyor…' }));

  let rows: AlbumSummary[];
  try {
    rows = (await albums.list()).albums;
  } catch (err) {
    ctx.fail(err);
    return;
  }

  const table =
    rows.length === 0
      ? el('p', { class: 'note', text: 'Henüz albüm yok.' })
      : el(
          'table',
          {},
          el(
            'thead',
            {},
            el(
              'tr',
              {},
              el('th', { text: 'albüm' }),
              el('th', { text: 'kategori' }),
              el('th', { text: 'yıl' }),
              el('th', { text: 'kare' }),
              el('th', { text: 'durum' }),
              el('th', { text: 'güncellendi' })
            )
          ),
          el(
            'tbody',
            {},
            ...rows.map((row) =>
              el(
                'tr',
                {},
                el(
                  'td',
                  {},
                  el('a', {
                    href: `#/albumler/${encodeURIComponent(row.id)}`,
                    text: row.title.tr,
                    style: 'color:inherit'
                  })
                ),
                el('td', { class: 'dim', text: row.category.tr }),
                el('td', { class: 'dim', text: String(row.year) }),
                el('td', { text: String(row.photoCount) }),
                el('td', {
                  class: row.published ? 'ok' : 'dim',
                  text: row.published ? 'yayında' : 'taslak'
                }),
                el('td', { class: 'dim', text: when(row.updatedAt) })
              )
            )
          )
        );

  replace(
    host,
    el(
      'div',
      { class: 'row' },
      el('h1', { text: 'Albümler', style: 'margin:0' }),
      el('span', { class: 'spacer' }),
      el('button', {
        type: 'button',
        class: 'primary',
        text: 'Yeni albüm',
        onclick: () => void renderCreate(host, ctx)
      })
    ),
    el('div', { style: 'height:16px' }),
    table
  );
}

/* ── Yaratma ────────────────────────────────────────────── */

async function renderCreate(host: HTMLElement, ctx: AppContext): Promise<void> {
  const fields = {
    slug: el('input', { type: 'text', placeholder: 'deri-inci', required: true }),
    title: el('input', { type: 'text', required: true }),
    titleEn: el('input', { type: 'text' }),
    lede: el('textarea', { required: true }),
    category: el('input', { type: 'text', required: true }),
    year: el('input', { type: 'number', value: String(new Date().getFullYear()), required: true })
  };
  const errs: Record<string, HTMLElement> = {};
  for (const key of Object.keys(fields)) errs[key] = el('p', { class: 'err' });

  const save = el('button', { type: 'submit', class: 'primary', text: 'Oluştur' });

  const form = el(
    'form',
    {
      onsubmit: async (event: Event) => {
        event.preventDefault();
        for (const node of Object.values(errs)) node.textContent = '';
        save.disabled = true;
        try {
          const created = await albums.create({
            slug: fields.slug.value.trim(),
            title: {
              tr: fields.title.value.trim(),
              ...(fields.titleEn.value.trim() ? { en: fields.titleEn.value.trim() } : {})
            },
            lede: { tr: fields.lede.value.trim() },
            category: { tr: fields.category.value.trim() },
            year: Number(fields.year.value)
          });
          ctx.toast('Albüm oluşturuldu.');
          location.hash = `#/albumler/${encodeURIComponent(created.id)}`;
        } catch (err) {
          save.disabled = false;
          applyFieldErrors(err, errs, ctx);
        }
      }
    },
    el('label', {}, el('span', { text: 'Slug (adreste görünür)' }), fields.slug),
    errs['slug']!,
    el('label', {}, el('span', { text: 'Başlık (TR)' }), fields.title),
    errs['title']!,
    el('label', {}, el('span', { text: 'Başlık (EN, isteğe bağlı)' }), fields.titleEn),
    el('label', {}, el('span', { text: 'Giriş metni' }), fields.lede),
    errs['lede']!,
    el('label', {}, el('span', { text: 'Kategori' }), fields.category),
    errs['category']!,
    el('label', {}, el('span', { text: 'Yıl' }), fields.year),
    errs['year']!,
    el(
      'div',
      { class: 'row' },
      save,
      el('button', { type: 'button', text: 'Vazgeç', onclick: () => void renderList(host, ctx) })
    )
  );

  replace(host, el('h1', { text: 'Yeni albüm' }), el('div', { style: 'max-width:520px' }, form));
  fields.slug.focus();
}

/* ── Düzenleme ──────────────────────────────────────────── */

async function renderEditor(host: HTMLElement, ctx: AppContext, id: string): Promise<void> {
  replace(host, el('p', { class: 'note', text: 'Okunuyor…' }));

  let album: AlbumDetail;
  let library: PhotoRow[];
  try {
    [album, library] = await Promise.all([
      albums.read(id),
      photos.list({ limit: 120 }).then((p) => p.photos)
    ]);
  } catch (err) {
    ctx.fail(err);
    return;
  }

  /* Ekrandaki sıra burada duruyor; sunucuya ancak "Sırayı kaydet" ile
     gidiyor. Her sürüklemede istek atmak yarım bir düzenlemeyi yazardı. */
  let order = [...album.photoIds];
  let dirty = false;

  const strip = el('div', { class: 'strip' });
  const grid = el('div', { class: 'grid' });
  const saveOrder = el('button', {
    type: 'button',
    class: 'primary',
    text: 'Sırayı kaydet',
    disabled: true
  });

  const byId = new Map(library.map((p) => [p.id, p]));

  const markDirty = (): void => {
    dirty = true;
    saveOrder.disabled = false;
  };

  const drawStrip = (): void => {
    if (order.length === 0) {
      replace(strip, el('p', { class: 'note', text: 'Albümde kare yok. Aşağıdan seçin.' }));
      return;
    }
    replace(
      strip,
      ...order.map((photoId, index) =>
        drawChip(photoId, index, {
          onMove: (from, to) => {
            const moved = order.splice(from, 1)[0]!;
            order.splice(to, 0, moved);
            markDirty();
            drawStrip();
            drawGrid();
          },
          onRemove: (at) => {
            order.splice(at, 1);
            markDirty();
            drawStrip();
            drawGrid();
          }
        })
      )
    );
  };

  const drawGrid = (): void => {
    replace(
      grid,
      ...library.map((photo) => {
        const chosen = order.includes(photo.id);
        return el(
          'button',
          {
            type: 'button',
            class: 'cell',
            'aria-pressed': String(chosen),
            title: photo.id,
            onclick: () => {
              if (chosen) order = order.filter((x) => x !== photo.id);
              else order.push(photo.id);
              markDirty();
              drawStrip();
              drawGrid();
            }
          },
          /* Gezinme katmanı herkese açık kovada; imza gerekmiyor (plan §5.1).
             Kare henüz yüklenmemişse tarayıcı kırık görsel gösterir — o yüzden
             kimlik ayrıca yazılıyor. */
          el('img', {
            src: `https://photos.frawgraphy.com/browse/${encodeURIComponent(photo.id)}.webp`,
            alt: photo.alt.tr,
            loading: 'lazy'
          }),
          el('figcaption', { text: photo.id })
        );
      })
    );
  };

  saveOrder.onclick = async () => {
    saveOrder.disabled = true;
    try {
      await albums.reorder(album.id, order);
      dirty = false;
      ctx.toast('Sıra kaydedildi.');
    } catch (err) {
      saveOrder.disabled = false;
      ctx.fail(err);
    }
  };

  const publishedBox = el('input', { type: 'checkbox', ...(album.published ? { checked: true } : {}) });
  publishedBox.onchange = async () => {
    try {
      await albums.patch(album.id, { published: publishedBox.checked });
      ctx.toast(publishedBox.checked ? 'Albüm yayında.' : 'Albüm taslağa alındı.');
    } catch (err) {
      publishedBox.checked = !publishedBox.checked;
      ctx.fail(err);
    }
  };

  const remove = el('button', {
    type: 'button',
    class: 'danger',
    text: 'Albümü sil',
    onclick: async () => {
      /* Onay isteniyor: silme geri alınamaz ve albüm bir kampanyanın tamamı
         olabilir. Kareler duruyor, yalnızca albüm ve üyelikler gidiyor. */
      if (!confirm(`"${album.title.tr}" silinsin mi? Kareler arşivde kalır.`)) return;
      try {
        await albums.remove(album.id);
        ctx.toast('Albüm silindi.');
        location.hash = '#/albumler';
      } catch (err) {
        ctx.fail(err);
      }
    }
  });

  addEventListener('beforeunload', (event) => {
    if (!dirty) return;
    /* Kaydedilmemiş sıra kaybolmasın. Tarayıcı metni kendi yazıyor. */
    event.preventDefault();
    event.returnValue = '';
  });

  replace(
    host,
    el(
      'div',
      { class: 'row' },
      el('a', { href: '#/albumler', text: '← Albümler', class: 'note', style: 'color:inherit' }),
      el('span', { class: 'spacer' }),
      el('label', { style: 'margin:0;display:flex;gap:7px;align-items:center' }, publishedBox, el('span', { text: 'Yayında', style: 'margin:0' })),
      remove
    ),
    el('h1', { text: album.title.tr, style: 'margin-top:16px' }),
    el('p', { class: 'note', text: `${album.id} · ${album.category.tr} · ${album.year}` }),
    el('h2', { text: 'Albümdeki kareler' }),
    strip,
    el('div', { class: 'row', style: 'margin-top:12px' }, saveOrder),
    el('h2', { text: 'Arşivden seç' }),
    grid
  );

  drawStrip();
  drawGrid();

  function drawChip(
    photoId: string,
    index: number,
    on: { onMove: (from: number, to: number) => void; onRemove: (at: number) => void }
  ): HTMLElement {
    const photo = byId.get(photoId);
    const chip = el(
      'div',
      {
        class: 'chip',
        draggable: true,
        'data-index': String(index)
      },
      el('span', { class: 'pos', text: String(index + 1) }),
      el('span', { text: photo ? photo.id : `${photoId} (arşivde yok)` }),
      /* Klavye ve dokunmatik yolu: sürüklemek erişilebilir bir etkileşim
         değil, o yüzden her karenin taşıma düğmeleri var. */
      el('button', {
        type: 'button',
        title: 'Sola taşı',
        text: '‹',
        disabled: index === 0,
        onclick: () => on.onMove(index, index - 1)
      }),
      el('button', {
        type: 'button',
        title: 'Sağa taşı',
        text: '›',
        disabled: index === order.length - 1,
        onclick: () => on.onMove(index, index + 1)
      }),
      el('button', { type: 'button', title: 'Albümden çıkar', text: '×', onclick: () => on.onRemove(index) })
    );

    chip.addEventListener('dragstart', (event) => {
      chip.setAttribute('data-dragging', '');
      event.dataTransfer?.setData('text/plain', String(index));
      if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    });
    chip.addEventListener('dragend', () => chip.removeAttribute('data-dragging'));
    chip.addEventListener('dragover', (event) => {
      event.preventDefault();
      chip.setAttribute('data-drop-before', '');
    });
    chip.addEventListener('dragleave', () => chip.removeAttribute('data-drop-before'));
    chip.addEventListener('drop', (event) => {
      event.preventDefault();
      chip.removeAttribute('data-drop-before');
      const from = Number(event.dataTransfer?.getData('text/plain'));
      if (Number.isInteger(from) && from !== index) on.onMove(from, index);
    });
    return chip;
  }
}

/** Sunucunun alan hatalarını formun altına dağıtır; kalanı bildirime düşer. */
function applyFieldErrors(
  err: unknown,
  errs: Record<string, HTMLElement>,
  ctx: AppContext
): void {
  const fields = (err as { fields?: Record<string, string> }).fields ?? {};
  let placed = false;
  for (const [key, node] of Object.entries(errs)) {
    const message = fields[key] ?? fields[`${key}.tr`];
    if (message) {
      node.textContent = message;
      placed = true;
    }
  }
  if (!placed) ctx.fail(err);
}
